(() => {
  if (window.LibertasSizeSystem?.initialized) return;

  const configElement = document.querySelector('[data-size-system-config]');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }
  if (!config?.enabled) return;

  const systems = ['EU', 'UK', 'US'];
  const storageKey = config.storageKey || 'libertas:size-system:v1';

  const normalizeSize = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    const numericMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:EU)?$/i);
    return numericMatch ? String(Number(numericMatch[1])) : normalized.toLocaleLowerCase();
  };

  const formatNumericSize = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    return /^-?\d+(?:\.\d+)?$/.test(normalized) ? String(Number(normalized)) : String(value ?? '').trim();
  };

  const conversionTables = new Map();

  const getConversionTable = (tableSource) => {
    const source = String(tableSource || '').trim();
    if (conversionTables.has(source)) return conversionTables.get(source);

    const table = new Map();
    source.split(/\r?\n/).forEach((row) => {
      const cells = row.split('|').map((cell) => cell.trim());
      if (cells.length < 4 || !cells[0]) return;
      table.set(normalizeSize(cells[0]), {
        EU: formatNumericSize(cells[0]),
        measurement: cells[1],
        UK: formatNumericSize(cells[2]),
        US: formatNumericSize(cells[3]),
      });
    });
    conversionTables.set(source, table);
    return table;
  };

  const getTableSource = (element) => element.closest('[data-size-table]')?.dataset.sizeTable || '';

  const readPreference = () => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      return systems.includes(saved) ? saved : 'EU';
    } catch {
      return 'EU';
    }
  };

  let currentSystem = readPreference();

  const getConversion = (euSize, tableSource = '') => getConversionTable(tableSource).get(normalizeSize(euSize));

  const formatSize = (euSize, system = currentSystem, tableSource = '') => {
    const conversion = getConversion(euSize, tableSource);
    if (!conversion) return String(euSize ?? '');
    return conversion[system] || conversion.EU || String(euSize ?? '');
  };

  const isSizeOption = (name) => {
    const normalized = String(name || '').trim().toLocaleLowerCase();
    return ['talla', 'size', 'taille', 'größe', 'grosse', 'taglia'].some((term) => normalized.includes(term));
  };

  const updateControls = (root) => {
    root.querySelectorAll('[data-size-system-switcher]').forEach((switcher) => {
      switcher.dataset.activeSystem = currentSystem;
      switcher.querySelectorAll('[data-size-system-button]').forEach((button) => {
        const active = button.dataset.sizeSystemButton === currentSystem;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    });
  };

  const updateProductValues = (root) => {
    root.querySelectorAll('[data-product-size-value]').forEach((value) => {
      value.textContent = formatSize(value.dataset.euSize, currentSystem, getTableSource(value));
    });
  };

  const updateCartValues = (root) => {
    root.querySelectorAll('[data-cart-option]').forEach((option) => {
      const optionName = option.dataset.optionName || '';
      if (!isSizeOption(optionName)) return;

      const value = option.querySelector('[data-cart-option-value]');
      const label = option.querySelector('[data-cart-option-label]');
      if (!value || !label) return;

      const conversion = getConversion(value.dataset.euSize, getTableSource(option));
      const converted = Boolean(conversion?.[currentSystem]);
      value.textContent = converted ? conversion[currentSystem] : value.dataset.euSize;
      label.textContent = currentSystem !== 'EU' && converted
        ? `${optionName} (${currentSystem})`
        : optionName;
    });
  };

  const apply = (root = document) => {
    document.documentElement.dataset.sizeSystem = currentSystem;
    updateControls(root);
    updateProductValues(root);
    updateCartValues(root);
  };

  const setSystem = (system, persist = true) => {
    if (!systems.includes(system)) return;
    currentSystem = system;
    if (persist) {
      try {
        window.localStorage.setItem(storageKey, system);
      } catch {
        // Storage can be unavailable in privacy modes; the current page still updates.
      }
    }
    apply(document);
    document.dispatchEvent(new CustomEvent('size-system:change', { detail: { system } }));
  };

  window.LibertasSizeSystem = {
    initialized: true,
    apply,
    formatSize,
    getSystem: () => currentSystem,
    setSystem,
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-size-system-button]');
    if (!button) return;
    setSystem(button.dataset.sizeSystemButton);
  });

  document.addEventListener('cart:updated', () => requestAnimationFrame(() => apply(document)));
  document.addEventListener('shopify:section:load', (event) => apply(event.target));
  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey || !systems.includes(event.newValue)) return;
    setSystem(event.newValue, false);
  });

  apply(document);
})();
